import { FenceUniqueCount, GeoDataItem, Geofence } from "../../types";
import { isUsableCoord, LatLng } from "../../utils/map-view";

export type TimeRange = { key: string; label: string; daysAgo: number; hoursAgo: number };

export const TIME_RANGES: TimeRange[] = [
  { key: "1h", label: "Last hour", daysAgo: 0, hoursAgo: 1 },
  { key: "24h", label: "24 hours", daysAgo: 0, hoursAgo: 24 },
  { key: "7d", label: "Last 7 days", daysAgo: 7, hoursAgo: 0 },
  { key: "30d", label: "Last 30 days", daysAgo: 30, hoursAgo: 0 },
  { key: "120d", label: "Last 120 days", daysAgo: 120, hoursAgo: 0 },
];

export type MapFence = {
  locationId: string;
  name: string;
  coordinates: LatLng[];
  /** Where the marker sits; null when the fence has no usable coordinates. */
  centre: LatLng | null;
};

/** Above this many distinct members a venue gets the gold highlight. */
export const HIGH_COUNT_THRESHOLD = 50;

/** none: 0 (muted), active: 1 to 50 (blue), high: over 50 (gold). */
export type CountTone = "none" | "active" | "high";

export const countTone = (count: number): CountTone => {
  if (!(count > 0)) return "none";
  return count > HIGH_COUNT_THRESHOLD ? "high" : "active";
};

/** Venues in a tier with at least one member, for the "N active" badge. */
export const activeVenueCount = (counts: FenceUniqueCount[]): number =>
  counts.filter((c) => c.distinctUserCount > 0).length;

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });

/**
 * Tiers in tier order, with each tier's venues alphabetical, so a venue sits on the same row in
 * every panel and in the same position as its chip.
 */
export const sortTiers = (tiers: GeoDataItem[]): GeoDataItem[] =>
  [...tiers]
    .sort((a, b) => a.tierNumber - b.tierNumber)
    .map((tier) => ({
      ...tier,
      fenceUniqueCounts: [...(tier.fenceUniqueCounts ?? [])].sort((a, b) =>
        byName(a.geofenceName, b.geofenceName)
      ),
    }));

/**
 * The coloured strip above each tier panel. Tier names differ per vendor, so this matches on the
 * colour word in the name and falls back to neutral for anything else. Whole words only, so a
 * tier such as "Preferred" is not taken for red.
 */
const TIER_STRIPS: [RegExp, string][] = [
  [/\bwhite\b/i, "bg-slate-400"],
  [/\bred\b/i, "bg-red-500"],
  [/\bblue\b/i, "bg-blue-500"],
  [/\bgold(en)?\b/i, "bg-amber-500"],
  [/\bpink\b/i, "bg-pink-500"],
  [/\bcrystal\b/i, "bg-cyan-500"],
  [/\b(purple|violet)\b/i, "bg-violet-500"],
  [/\bblack\b/i, "bg-neutral-800"],
  [/\b(platinum|silver)\b/i, "bg-slate-300"],
];

export const tierStripClass = (tierName: string): string =>
  TIER_STRIPS.find(([pattern]) => pattern.test(tierName))?.[1] ?? "bg-neutral-400";

/** Centre of the fence's bounding box, ignoring 0,0 and other unusable points. */
export const fenceCentre = (coordinates: LatLng[]): LatLng | null => {
  const usable = coordinates.filter(isUsableCoord);
  if (usable.length === 0) return null;

  const lats = usable.map((c) => c.lat);
  const lngs = usable.map((c) => c.lng);
  return {
    lat: (Math.min(...lats) + Math.max(...lats)) / 2,
    lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
  };
};

export const toMapFence = (fence: Geofence): MapFence => {
  const coordinates = (fence.coordinates ?? []).map((c) => ({
    lat: c.latitude,
    lng: c.longitude,
  }));
  return {
    locationId: fence.locationId,
    name: fence.name,
    coordinates,
    centre: fenceCentre(coordinates),
  };
};

export const sortFences = (fences: MapFence[]): MapFence[] =>
  [...fences].sort((a, b) => byName(a.name, b.name));
